// src/lib/colors.ts
// Centralized palette. Every pill, badge, or calendar entry must pick
// its visual treatment by tone — never by re-deriving colors locally.

import type { Tone } from "@shared/labels";
import type { CommissionStatus } from "@shared/schema";
export type { Tone };

// Commission amount text color by state — shared across Ventes & Dashboard.
// gold while waiting on rétractation, red once due, green once paid.
export const COMMISSION_TEXT_COLOR: Record<CommissionStatus, string> = {
  en_attente_retractation: "text-[#ca8a04]",
  du: "text-[var(--color-coral-600)]",
  paye: "text-[#16a34a]",
  annule: "text-[var(--color-muted-foreground)]",
};

export interface ToneStyle {
  /** Soft background + dark foreground, e.g. for pills. */
  pillBg: string;
  pillFg: string;
  /** Solid dot for the leading indicator in a pill. */
  dot: string;
  /** Left-border accent color for cards. */
  border: string;
  /** Lighter card background (cards in lists). */
  cardBg: string;
  cardHover: string;
  cardBorder: string;
}

// Per-tone pillBg color-mix percentages vary deliberately:
// - navy at 10%: navy is the darkest brand color, a lighter mix keeps text
//   contrast high while staying subtle
// - coral at 14%: coral is a saturated brand accent that needs more
//   presence in CTAs
// - all other tones at 12% (default soft-pill weight)
export const TONE_STYLES: Record<Tone, ToneStyle> = {
  navy: {
    pillBg: "bg-[color-mix(in_srgb,var(--color-navy-600)_10%,white)]",
    pillFg: "text-[var(--color-navy-700)]",
    dot: "bg-[var(--color-navy-600)]",
    border: "border-l-[var(--color-navy-600)]",
    cardBg: "bg-[var(--color-navy-50)]",
    cardHover: "hover:bg-[var(--color-navy-100)]",
    cardBorder: "border-[var(--color-navy-200)]",
  },
  sky: {
    pillBg: "bg-[color-mix(in_srgb,#0284c7_12%,white)]",
    pillFg: "text-[#0c4a6e]",
    dot: "bg-[#0284c7]",
    border: "border-l-[#0284c7]",
    cardBg: "bg-[color-mix(in_srgb,#0284c7_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#0284c7_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#0284c7_30%,white)]",
  },
  yellow: {
    pillBg: "bg-[color-mix(in_srgb,#eab308_16%,white)]",
    pillFg: "text-[#854d0e]",
    dot: "bg-[#ca8a04]",
    border: "border-l-[#ca8a04]",
    cardBg: "bg-[color-mix(in_srgb,#eab308_10%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#eab308_18%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#ca8a04_35%,white)]",
  },
  orange: {
    pillBg: "bg-[color-mix(in_srgb,#ea580c_12%,white)]",
    pillFg: "text-[#9a3412]",
    dot: "bg-[#ea580c]",
    border: "border-l-[#ea580c]",
    cardBg: "bg-[color-mix(in_srgb,#ea580c_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#ea580c_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#ea580c_30%,white)]",
  },
  violet: {
    pillBg: "bg-[color-mix(in_srgb,#7c3aed_14%,white)]",
    pillFg: "text-[#5b21b6]",
    dot: "bg-[#7c3aed]",
    border: "border-l-[#7c3aed]",
    cardBg: "bg-[color-mix(in_srgb,#7c3aed_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#7c3aed_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#7c3aed_30%,white)]",
  },
  coral: {
    pillBg: "bg-[color-mix(in_srgb,var(--color-coral-500)_14%,white)]",
    pillFg: "text-[var(--color-coral-700)]",
    dot: "bg-[var(--color-coral-500)]",
    border: "border-l-[var(--color-coral-500)]",
    cardBg: "bg-[color-mix(in_srgb,var(--color-coral-500)_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,var(--color-coral-500)_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,var(--color-coral-500)_30%,white)]",
  },
  green: {
    pillBg: "bg-[color-mix(in_srgb,#16a34a_12%,white)]",
    pillFg: "text-[#0f6b30]",
    dot: "bg-[#16a34a]",
    border: "border-l-[#16a34a]",
    cardBg: "bg-[color-mix(in_srgb,#16a34a_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#16a34a_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#16a34a_30%,white)]",
  },
  red: {
    pillBg: "bg-[color-mix(in_srgb,#dc2626_12%,white)]",
    pillFg: "text-[#991b1b]",
    dot: "bg-[#dc2626]",
    border: "border-l-[#dc2626]",
    cardBg: "bg-[color-mix(in_srgb,#dc2626_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#dc2626_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#dc2626_30%,white)]",
  },
  slate: {
    pillBg: "bg-[color-mix(in_srgb,#64748b_12%,white)]",
    pillFg: "text-[#475569]",
    dot: "bg-[#64748b]",
    border: "border-l-[#64748b]",
    cardBg: "bg-[color-mix(in_srgb,#64748b_8%,white)]",
    cardHover: "hover:bg-[color-mix(in_srgb,#64748b_14%,white)]",
    cardBorder: "border-[color-mix(in_srgb,#64748b_30%,white)]",
  },
};

export const TONE_LABEL_FR: Record<Tone, string> = {
  navy: "Par défaut",
  sky: "Planifié",
  yellow: "En attente",
  orange: "Accent",
  violet: "Relance",
  coral: "À traiter",
  green: "Gagné",
  red: "Annulé",
  slate: "Perdu",
};
